// src/pages/QuotationRecord/components/QuotationRecordTable.js
import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tooltip, Popconfirm, message, Grid } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  deleteQuotationRecord,
  fetchQuotationRecord,
  fetchQuotationWithDetails,
} from "../QuotationRecordThunk";

import {
  setIsEditQuotationRecord,
  setSelectedQuotationRecord,
} from "../QuotationRecordSlice";

import { loadQuotationForSalesOrder } from "../../Order/Sales Order/Sales Order Entry/salesOrderSlice";
import { formatToCompanyTimezone } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { loadQuotationForEdit } from "../../Quotation/quotationSlice";
import { useTxLock } from "../../../hooks/useTxLock";
import VoucherPdfActions from "../../../components/common/VoucherPdfActions";

const { useBreakpoint } = Grid;

const QuotationRecordTable = ({ printMode = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const authInfo = useSelector(selectAuthInfo);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  const { list, loading, filters } = useSelector((state) => state.quotationRecord);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // const [isAdminUser, isAdminUserSet] = useState(false);
  // const { list, loading, filters } = useSelector(
  //   (state) => state.quotationRecord,
  // );
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  // ✅ handle delete
  const handleDelete = async (id = list.sale_o_id) => {
    try {
      const res = await dispatch(deleteQuotationRecord(id));
      if (!res?.data?.error) {
        message.success("Quotation deleted successfully ✅");
        // refresh table
        dispatch(
          fetchQuotationRecord({
            selectedFilterType: filters.filterType,
            selectedRecordType: filters.recordType,
            filters,
          }),
        );
      } else {
        message.error(res.data.message || "Failed to delete ❌");
      }
    } catch (err) {
      message.error("Delete failed ❌");
    }
  };

  // ✅ handle conversion
  const handleConvert = (id, type) => {
    if (type === "sale") {
      dispatch(fetchQuotationWithDetails(id)).then((res) => {
       if (res.meta.requestStatus === "fulfilled") {
        const isPharma =
          authInfo?.company_type === "Pharmacy";

        navigate(
          isPharma
            ? `/sales/pharma-quotation-to-sales/${id}`
            : `/sales/quotation-to-sales/${id}`
        );
      }
      });
    } else {
      dispatch(fetchQuotationWithDetails(id)).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          dispatch(loadQuotationForSalesOrder(res.payload));
          navigate(`/order/quotation-to-order/${id}`);
        }
      });
    }
  };
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    { title: "VCH No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Customer / Debtor", dataIndex: "customer" },
    // { title: "Employee", dataIndex: "employee" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Sub Total", dataIndex: "subtotal" },
    { title: "Discount", dataIndex: "discount" },
    showTax && { title: taxLabel, dataIndex: "vat" },
    { title: "Total", dataIndex: "total" },

    // 🔹 ACTIONS column (View, Edit, Delete)
!printMode && {
  title: "Actions",
  align: "center",
  width: 170,
  render: (_, record) => {
    const txLocked = isTxLocked(record?.date);

    return (
      <Space style={{ flexWrap: "nowrap" }}>
        <Tooltip title="View Quotation">
          <Link to={`/quotation/quick-quotation-voucher/${record.q_id}`}>
            <Button
              icon={<EyeOutlined />}
              type="text"
              style={{ color: "#1677ff" }}
            />
          </Link>
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit Quotation"
          }
        >
          <Button
            icon={<EditOutlined />}
            type="text"
            style={{
              color: (isAdminUser || canEdit) ? "green" : "#ccc",
            }}
            disabled={!(isAdminUser || canEdit) || txLocked}
            onClick={() => {
              dispatch(setSelectedQuotationRecord(record));
              dispatch(setIsEditQuotationRecord(true));

              dispatch(fetchQuotationWithDetails(record.q_id)).then(
                (res) => {
                  if (res.meta.requestStatus === "fulfilled") {
                    const quotationData = Array.isArray(res.payload)
                      ? res.payload[0]
                      : res.payload;

                    dispatch(
                      loadQuotationForEdit({
                        quotation: quotationData,
                        tags: quotationData?.tags || [],
                      })
                    );

                    navigate("/quotation/quotation");
                  }
                }
              );
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete Quotation"
          }
        >
          <Popconfirm
            title="Are you sure you want to delete this quotation?"
            disabled={!(isAdminUser || canDelete) || txLocked}
            onConfirm={() => handleDelete(record.q_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              disabled={!(isAdminUser || canDelete) || txLocked}
              icon={<DeleteOutlined />}
              type="text"
              style={{
                color: (isAdminUser || canDelete) ? "red" : "#ccc",
              }}
            />
          </Popconfirm>
        </Tooltip>

        <VoucherPdfActions
          url={`/print/quotation/${record.q_id}`}
          fileName={`Quotation_${record.vch_no || record.q_id}.pdf`}
        />
      </Space>
    );
  },
},

!printMode && {
  title: "Convert",
  align: "center",
  width: !screens.xs && 250,
  render: (_, record) => {
    const txLocked = isTxLocked(record?.date);

    return (
      <Space>
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Convert to Sale Order"
          }
        >
          <Button
            icon={<SwapOutlined />}
            disabled={txLocked}
            onClick={() => {
              if (txLocked) return;

              handleConvert(record.id, "order");
            }}
          >
            {!screens.xs && "ORDER"}
          </Button>
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Convert to Sale"
          }
        >
          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            disabled={txLocked}
            onClick={() => {
              if (txLocked) return;

              handleConvert(record.q_id, "sale");
            }}
          >
            {!screens.xs && "SALE"}
          </Button>
        </Tooltip>
      </Space>
    );
  },
},
  ].filter(Boolean);

  return (
    <Table
      rowKey="id"
      size={screens.xs ? "small" : "default"}
      columns={columns}
      dataSource={list}
      loading={loading}
      data-testid="quotation-record-table"
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}

      scroll={printMode ? undefined : { x: columns.reduce((sum, col) => sum + (col.width || 150), 0), y: "65vh" }}

      bordered


      summary={() => {
        const totals = list.reduce(
          (acc, row) => {
            acc.subtotal += Number(row.subtotal || 0);
            acc.discount += Number(row.discount || 0);
            acc.vat += Number(row.vat || 0);
            acc.total += Number(row.total || 0);
            return acc;
          },
          { subtotal: 0, discount: 0, vat: 0, total: 0 },
        );

        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}>
              <strong>Grand Total</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6}>
              {totals.subtotal.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7}>
              {totals.discount.toFixed(2)}
            </Table.Summary.Cell>
            {showTax && (
              <Table.Summary.Cell index={8}>
                {totals.vat.toFixed(2)}
              </Table.Summary.Cell>
            )}
            <Table.Summary.Cell index={showTax ? 9 : 8}>
              {totals.total.toFixed(2)}
            </Table.Summary.Cell>
            {!printMode && (
              <>
                <Table.Summary.Cell index={showTax ? 10 : 9}></Table.Summary.Cell>
                <Table.Summary.Cell index={showTax ? 11 : 10}></Table.Summary.Cell>
              </>
            )}
          </Table.Summary.Row>
        );
      }}
    />
  );
};

export default QuotationRecordTable;
